import {AppState} from '../states/app.state';
import {createSelector} from '@ngrx/store';
import {identity} from 'rxjs';
import {DeinBetriebBlockData} from '../../interfaces/dein-betrieb-block-data.interface';
import {BetriebsadresseBlockData} from '../../interfaces/betriebsadresse-block-data.interface';
import {BetriebsinformationenBlockData} from '../../interfaces/betriebsinformationen-block-data.interface';
import {BankkontoUpdate} from '../../interfaces/bankkonto-update.interface';
import {BankkontoCreateRequestDTO} from '../../openapi/zahlung-openapi';


export class FirmaEditSelectors {

  private static select = {
    requestDto: (state: AppState) => state.firmaEdit.requestDto,
    selectedFinanzamtDto: (state: AppState) => state.firmaEdit.selectedFinanzamtDto,
    formBlockDeinBetriebValid: (state: AppState) => state.firmaEdit.formBlockDeinBetriebValid,
    formBetriebsadresseValid: (state: AppState) => state.firmaEdit.formBetriebsadresseValid,
    formBetriebsinformationenValid: (state: AppState) => state.firmaEdit.formBetriebsinformationenValid,
    formBlockKontaktValid: (state: AppState) => state.firmaEdit.formBlockKontaktValid,
    formBlockBankkontoValid: (state: AppState) => state.firmaEdit.formBlockBankkontoValid,
    formBlockBelegdetailsValid: (state: AppState) => state.firmaEdit.formBlockBelegdetailsValid,
    besteuerungsform: (state: AppState) => state.firmaEdit.einstellungenRequestDto?.besteuerungsform,
    vvvoNummer: (state: AppState) => state.firmaEdit.einstellungenRequestDto?.vvvoNummer,
    oekoKontrollnummer: (state: AppState) => state.firmaEdit.einstellungenRequestDto?.oekoKontrollnummer,
    bankkonten: (state: AppState) => state.firmaEdit.bankkonten,
    selectedBankkontoDto: (state: AppState) => state.firmaEdit.selectedBankkontoDto,
    bankkontoCreateRequestDTO: (state: AppState) => state.firmaEdit.bankkontoCreateRequestDTO,
    kontoinhaber: (state: AppState) => state.firmaEdit.kontoinhaber,
    einstellungenRequestDto: (state: AppState) => state.firmaEdit.einstellungenRequestDto,
    einstellungenResponseDto: (state: AppState) => state.firmaEdit.einstellungenResponseDto,
    // INFO: Der Request-Dto trägt die aktuelle Auswahl; der Response-Dto hält den Stand vor dem Speichern
    //  und würde die Optionen mitten in der Speicher-Kette auf das alte Konto zurückwerfen.
    selectedBankkontoId: (state: AppState) => state.firmaEdit.einstellungenRequestDto?.bankkontoId,
    updateFirmaPending: (state: AppState) => state.firmaEdit.updateFirmaPending,
    updateFirmaSuccessful: (state: AppState) => state.firmaEdit.updateFirmaSuccessful,
  };

  static requestDto = createSelector(
    FirmaEditSelectors.select.requestDto,
    identity,
  );

  static deinBetriebBlockData = createSelector(
    FirmaEditSelectors.requestDto,
    requestDto => {
      const blockData: DeinBetriebBlockData = {
        firmenbezeichnung: requestDto?.firmenbezeichnung,
        geschaeftstaetigkeit: requestDto?.geschaeftstaetigkeit,
      };

      return blockData;
    },
  );

  static betriebsadresseBlockData = createSelector(
    FirmaEditSelectors.requestDto,
    requestDto => {
      const blockData: BetriebsadresseBlockData = {
        adresse: requestDto?.adresse,
      };

      return blockData;
    },
  );

  static betriebsinformationenBlockData = createSelector(
    FirmaEditSelectors.requestDto,
    requestDto => {
      if (!requestDto) return;

      // INFO: Es muss das Finanzamt mit der Finanzamtsart 'Betrieb' ausgewählt werden, wenn vorhanden.
      let steuernummerDtos = requestDto.steuernummern;
      let steuernummerFinanzamtDto = steuernummerDtos?.find(steuernummerFinanzamtDto => {
        return steuernummerFinanzamtDto.finanzamtsartBetrieb;
      });

      const blockData: BetriebsinformationenBlockData = {
        steuernummerFinanzamtDto,
        umsatzsteuerIdNummer: requestDto.umsatzsteuerIdNummer,
        glaeubigerId: requestDto.glaeubigerId,
        isFieldSteuernummerDisabled: steuernummerFinanzamtDto !== undefined,
      };

      return blockData;
    },
  );

  static selectedFinanzamtDto = createSelector(
    FirmaEditSelectors.select.selectedFinanzamtDto,
    identity,
  );

  static isFormValid = createSelector(
    FirmaEditSelectors.select.formBlockDeinBetriebValid,
    FirmaEditSelectors.select.formBetriebsadresseValid,
    FirmaEditSelectors.select.formBetriebsinformationenValid,
    (formBlockDeinBetriebValid, formBetriebsadresseValid, formBetriebsinformationenValid) => {
      return formBlockDeinBetriebValid && formBetriebsadresseValid && formBetriebsinformationenValid;
    },
  );

  static isFormFakturierungValid = createSelector(
    FirmaEditSelectors.select.formBlockDeinBetriebValid,
    FirmaEditSelectors.select.formBetriebsadresseValid,
    FirmaEditSelectors.select.formBetriebsinformationenValid,
    FirmaEditSelectors.select.formBlockKontaktValid,
    FirmaEditSelectors.select.formBlockBankkontoValid,
    FirmaEditSelectors.select.formBlockBelegdetailsValid,
    (formBlockDeinBetriebValid, formBetriebsadresseValid, formBetriebsinformationenValid, formBlockKontaktValid, formBlockBankkontoValid, formBlockBelegdetailsValid) => {
      return formBlockDeinBetriebValid && formBetriebsadresseValid && formBetriebsinformationenValid && formBlockKontaktValid && formBlockBankkontoValid && formBlockBelegdetailsValid;
    },
  );

  static besteuerungsform = createSelector(
    FirmaEditSelectors.select.besteuerungsform,
    identity,
  );

  static vvvoNummer = createSelector(
    FirmaEditSelectors.select.vvvoNummer,
    identity,
  );

  static oekoKontrollnummer = createSelector(
    FirmaEditSelectors.select.oekoKontrollnummer,
    identity,
  );

  static bankkonten = createSelector(
    FirmaEditSelectors.select.bankkonten,
    identity,
  );

  static selectedBankkontoDto = createSelector(
    FirmaEditSelectors.select.selectedBankkontoDto,
    identity,
  );

  static bankkontoCreateRequestDTO = createSelector(
    FirmaEditSelectors.select.bankkontoCreateRequestDTO,
    identity,
  );

  static kontoinhaber = createSelector(
    FirmaEditSelectors.select.kontoinhaber,
    identity,
  );

  /**
   * Ermittelt die zu speichernde Aktualisierung des Kontoinhabers eines bereits existierenden Bankkontos.
   * Ergibt undefined bei Neuanlage, ohne persistiertes Bankkonto, ohne Kontoinhaber
   * oder wenn der Kontoinhaber unverändert ist.
   */
  static bankkontoUpdate = createSelector(
    FirmaEditSelectors.select.bankkontoCreateRequestDTO,
    FirmaEditSelectors.select.selectedBankkontoDto,
    FirmaEditSelectors.select.bankkonten,
    FirmaEditSelectors.select.kontoinhaber,
    (bankkontoCreateRequestDTO, selectedBankkontoDto, bankkonten, kontoinhaber): BankkontoUpdate | undefined => {
      if (bankkontoCreateRequestDTO) return undefined;

      const bankkontoId = selectedBankkontoDto?.id;
      if (!bankkontoId) return undefined;

      if (!kontoinhaber) return undefined;

      const originalBankkontoDto = bankkonten?.find(bankkonto => bankkonto.id === bankkontoId);
      if (!originalBankkontoDto) return undefined;

      if (kontoinhaber === originalBankkontoDto.kontoinhaber) return undefined;

      return {
        bankkontoId,
        requestDto: {
          kontoinhaber,
        },
      };
    },
  );

  /**
   * Ergänzt das Bankkonto-Create-Request-Dto um den im Formular erfassten Kontoinhaber.
   * Single Source of Truth dafür ist state.firmaEdit.kontoinhaber.
   */
  static bankkontoCreateRequestDtoForSave = createSelector(
    FirmaEditSelectors.select.bankkontoCreateRequestDTO,
    FirmaEditSelectors.select.kontoinhaber,
    (bankkontoCreateRequestDTO, kontoinhaber): BankkontoCreateRequestDTO | undefined => {
      if (!bankkontoCreateRequestDTO) return undefined;

      return {
        ...bankkontoCreateRequestDTO,
        kontoinhaber,
      };
    },
  );

  static einstellungenRequestDto = createSelector(
    FirmaEditSelectors.select.einstellungenRequestDto,
    identity,
  );

  static einstellungenResponseDto = createSelector(
    FirmaEditSelectors.select.einstellungenResponseDto,
    identity,
  );

  static selectedBankkontoId = createSelector(
    FirmaEditSelectors.select.selectedBankkontoId,
    identity,
  );

  static updateFirmaPending = createSelector(
    FirmaEditSelectors.select.updateFirmaPending,
    identity,
  );

  static updateFirmaSuccessful = createSelector(
    FirmaEditSelectors.select.updateFirmaSuccessful,
    identity,
  );
}
